import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useEffect, useState } from 'react';
import { ListItemText, Stack } from '@mui/material';
import AccountPopover from './header/AccountPopover';
import navConfig from './nav/config';

import brandlogoLight from '../../components/logo/VIRA LOGO - LIGHT MODE (002).png';
import brandlogoDark from '../../components/logo/VIRA LOGO - DARK MODE (002).png';
import NavSection from '../../components/nav-section/NavSection';

import { ThemeContext } from '../../theme';
import { useContext } from 'react';
import ThemeSwitch from '../../components/utils/ThemeSwitch';

import { StyledNavItem, StyledNavItemIcon } from '../../components/nav-section/styles';
import { NavLink } from 'react-router-dom';
import AddIcon from "@mui/icons-material/Add";

const drawerWidth = 270;

const openedMixin = (theme: Theme): CSSObject => ({
  marginTop: '80px',
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  background: theme.palette.mode === 'dark' ? '#1c1a27' : '#312d3b',
  color: '#fff',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  background: theme.palette.mode === 'dark' ? '#1c1a27' : '#312d3b',
  marginTop: '80px',
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(7)} + 1px)`,
    background: theme.palette.mode === 'dark' ? '#1c1a27' : '#312d3b',
    marginTop: '80px',
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-around',
  padding: theme.spacing(0, 1),
  background: '#fff',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  overflow: 'hidden',
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    // marginLeft: drawerWidth,
    // width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    // width: drawerWidth,
    flexShrink: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      // ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

export default function MainDrawer({ Nav, Outlet }: any) {
  const theme = useTheme();
  const themeContext = useContext(ThemeContext);
  const [open, setOpen] = useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    console.log('rendered');
  }, []);

  return (
    <Box sx={{ display: 'flex', background: theme.palette.background.paper }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} 
        sx ={{
          background: theme.palette.mode === 'dark' ? '#1c1a27' : '#fff',
          overflow: 'hidden', minWidth: '0'
      // background: "#fff",
        // color: "#000"
      }}>
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden', minWidth: '0'
            // justifyContent: 'space-between',
          }}
        >
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 3,
              color: 'text.primary',
              transition: 'all 0.3s ease',
            }}
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
          <div className='header'>
              <img src={theme.palette.mode === 'dark' ? brandlogoDark : brandlogoLight} alt='VIRA logo' style={{ width: "200px", height: "60px" }} className='logo' />            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconButton onClick={() => themeContext?.setMode?.(themeContext.mode === 'light' ? 'dark' : 'light')}>
                <ThemeSwitch />
              </IconButton>
              <Stack
                direction="row"
                alignItems="center"
                spacing={{
                  xs: 0.5,
                  sm: 1,
                }}
              >
                {/* <LanguagePopover /> */}
                {/* <NotificationsPopover /> */}
                <AccountPopover />
              </Stack>
            </Box>
          </div>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open} sx={{
        transition: 'width 0.3s ease',
        zIndex: '0',
        '&.active': {
          color: 'text.primary',
          bgcolor: 'action.selected',
          fontWeight: 'fontWeightBold',
        },
      }}>

        <NavSection data={navConfig} />
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          width: open ? '270px' : '57px',
          background: theme.palette.mode === 'dark' ? '#1c1a27' : '#312d3b',
          color: "#fff",
          transition: 'width 0.3s ease'
        }}>
          <StyledNavItem
            component={NavLink}
            to={`/dashboard/chat/0`}
          >
            <StyledNavItemIcon
              sx={{
                justifyContent: 'center',
                display: 'flex',
                alignItems: 'center',
              }}
            ><AddIcon
                sx={{

                }}
              /></StyledNavItemIcon>

            {open && (
              <ListItemText disableTypography primary={'New AI Search'} />
            )}
          </StyledNavItem>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 0, width: '100%', overflow: 'auto', minWidth: '0' }}>
        <DrawerHeader />
        <Box sx={{ width: '100%', paddingTop: 2 }}>
          {Outlet}
        </Box>
      </Box>
    </Box>
  );
}